import { SliceComponentProps } from "@prismicio/react";
import _get from "lodash/get";
import { useRouter } from "next/router";
import { useFormContext, RegisterOptions } from "react-hook-form";

import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import ChooseImage from "components/form/chooseImage/ChooseImage";
import { ChooseImageFieldSlice } from "prismicio-types";
import { useGetProduct } from "queries/product-queries";
import { FieldSliceContext, ChooseImageSchemaField } from "types/schema";
import {
  prismicFieldConfigurationError,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useInterpolate } from "utils/prismic-utils";
import {
  useFindByKey,
  isFieldType,
  useGetUpsellForField,
} from "utils/schema-utils";

import styles from "../Fields.module.scss";

export default function ChooseImageField({
  slice,
}: SliceComponentProps<ChooseImageFieldSlice, FieldSliceContext>) {
  const {
    formState: { errors },
  } = useFormContext();

  const { data: product } = useGetProduct();
  const router = useRouter();

  const findByKey = useFindByKey();
  const interpolate = useInterpolate();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const schemaKey = slice.primary.muse_schema_key;

  if (!schemaKey) {
    throw new Error("Missing Muse Schema Key");
  }

  const field = findByKey(schemaKey);
  const upsells = useGetUpsellForField(field?.urlKey);

  if (!field) {
    return null;
  }

  if (!isFieldType<ChooseImageSchemaField>(field, "choose_image")) {
    throw prismicFieldConfigurationError(
      schemaKey,
      "choose_image",
      field.metas[0].canvasComponent
    );
  }

  const registerOptions: RegisterOptions = {
    required:
      field.flags?.presence === "required" &&
      interpolate(
        product.content.canvas.form_field_required_error_message ||
          CANVAS_FALLBACK_CONTENT.form_field_required_error_message
      ),
  };

  // Show a warning message if there are upsells when editing an order
  const isDisabled = !!(upsells && router.query.order_id);
  const error = errors && _get(errors, field.urlKey)?.message;

  return (
    <div className={styles.field}>
      {slice.primary.label && (
        <label className={styles.label} htmlFor={schemaKey}>
          {interpolate(slice.primary.label)}
        </label>
      )}

      <ErrorBoundary>
        <ChooseImage
          aspectRatio={field.metas[0].aspectRatio}
          desktopColumns={slice.primary.desktop_columns ?? undefined}
          mobileColumns={slice.primary.columns ?? undefined}
          name={field.urlKey}
          options={field.metas[0].images}
          registerOptions={registerOptions}
          upsells={upsells}
        />
      </ErrorBoundary>

      {isDisabled && (
        <p className={styles.warning}>
          {interpolate(
            product.content.canvas.form_field_disabled_message ||
              CANVAS_FALLBACK_CONTENT.form_field_disabled_message
          )}
        </p>
      )}

      {error && <p className={styles.error}>{error as string}</p>}
    </div>
  );
}
